import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { isAuthenticated } from './api'
import {
  ADMIN_ROLES,
  ONBOARDING_ROLES,
  ANY_AUTHENTICATED_ROLES,
  MANAGER_ROLES,
  TESTER_OR_ADMIN_ROLES,
  defaultPathForRole,
  getRealm,
  getStoredAuthRole,
  isAdminRole,
} from './lib/realm'
import ErrorBoundary from './components/ErrorBoundary'
import Layout from './components/Layout'
import Login from './pages/Login'
import Models from './pages/Models'
import ModelDetail from './pages/ModelDetail'
import ModelDataPage from './pages/ModelDataPage'
import ChatsPage from './pages/ChatsPage'
import LogsPage from './pages/LogsPage'
import ProactiveLogsPage from './pages/ProactiveLogsPage'
import AnalyticsPage from './pages/AnalyticsPage'
import AutotestsPage from './pages/autotests/AutotestsPage'
import AutotestsPromptsPage from './pages/autotests/AutotestsPromptsPage'
import AutotestsRunDetailPage from './pages/autotests/AutotestsRunDetailPage'
import AutotestsAnalysisPage from './pages/autotests/AutotestsAnalysisPage'
import AutotestsAccessDeniedPage from './pages/autotests/AutotestsAccessDeniedPage'
import SettingsLayout from './pages/settings/SettingsLayout'
import SettingsConfiguration from './pages/settings/SettingsConfiguration'
import SettingsConfigDetailLayout from './pages/settings/SettingsConfigDetailLayout'
import SettingsAgentsLayout from './pages/settings/SettingsAgentsLayout'
import SettingsAgentDetail from './pages/settings/SettingsAgentDetail'
import SettingsNodesLayout from './pages/settings/SettingsNodesLayout'
import SettingsNodeDetail from './pages/settings/SettingsNodeDetail'
import SettingsProactive from './pages/settings/SettingsProactive'
import SettingsExecutorPage from './pages/settings/SettingsExecutorPage'
import SettingsConfigGraph from './pages/settings/SettingsConfigGraph'
import SettingsUsers from './pages/settings/SettingsUsers'
import AdminV2Layout from './components/v2/AdminV2Layout'
import AdminV2Automation from './pages/v2/AdminV2Automation'
import AdminV2Connections from './pages/v2/AdminV2Connections'
import AdminV2Content from './pages/v2/AdminV2Content'
import AdminV2Customs from './pages/v2/AdminV2Customs'
import AdminV2Dashboard from './pages/v2/AdminV2Dashboard'
import AdminV2Diagnostics from './pages/v2/AdminV2Diagnostics'
import AdminV2FanvueCallback from './pages/v2/AdminV2FanvueCallback'
import AdminV2Inbox from './pages/v2/AdminV2Inbox'
import AdminV2ModelDetail from './pages/v2/AdminV2ModelDetail'
import AdminV2Models from './pages/v2/AdminV2Models'
import AdminV2Onboarding from './pages/v2/AdminV2Onboarding'
import AdminV2PublicFanvueConnect from './pages/v2/AdminV2PublicFanvueConnect'
import AdminV2Settings from './pages/v2/AdminV2Settings'
import PublicInformationPage from './pages/PublicInformationPage'
import ChatterScorePage from './pages/ChatterScorePage'
import AgencySettingsPage from './pages/AgencySettingsPage'
import TrafficIntelligencePage from './pages/TrafficIntelligencePage'
import PartnerOnboardingPage from './pages/PartnerOnboardingPage'
import FanslyFypPage from './pages/FanslyFypPage'
import { I18nProvider } from './i18n'
import { ToastProvider } from './contexts/ToastContext'
import { WorkspaceScopeProvider, useWorkspaceScope } from './contexts/WorkspaceScopeContext'
import './styles/components/toast.css'

function ProtectedRoute({ children, realm }) {
  const currentRealm = realm ?? getRealm()
  if (!isAuthenticated(currentRealm)) {
    return <Navigate to="/login" replace />
  }
  return children
}

function IndexOrRedirect() {
  const role = getStoredAuthRole()
  if (role === 'agency') return <Navigate to="/old/agency-settings" replace />
  if (role === 'tester' || role === 'tester_no_media' || role === 'tester_plus') return <Navigate to="/old/fans" replace />
  return <Models />
}

function ModelDetailOrRedirect() {
  const role = getStoredAuthRole()
  if (role === 'tester' || role === 'tester_no_media' || role === 'tester_plus') return <Navigate to="/old/fans" replace />
  return <ModelDetail />
}

function LegacyV2Redirect({ target }) {
  const location = useLocation()
  const destination = typeof target === 'function' ? target(location.pathname) : target
  return <Navigate to={`${destination}${location.search || ''}${location.hash || ''}`} replace />
}

function SettingsOrRedirect() {
  if (!isAdminRole()) return <Navigate to={defaultPathForRole()} replace />
  return <SettingsLayout />
}

function AdminOnlyRoute({ children }) {
  if (!isAdminRole()) return <Navigate to={defaultPathForRole()} replace />
  return children
}

function RoleRoute({ allowed, children }) {
  const role = getStoredAuthRole()
  if (!allowed.includes(role)) return <Navigate to={defaultPathForRole(role)} replace />
  return children
}

function FanvueReviewRestrictedRoute({ children }) {
  const { loading, fanvueReviewWorkspace } = useWorkspaceScope()
  if (loading) return <div className="v2-loading">Loading workspace...</div>
  if (fanvueReviewWorkspace) return <Navigate to="/v2" replace />
  return children
}

function AutotestsRoute({ children }) {
  if (!TESTER_OR_ADMIN_ROLES.includes(getStoredAuthRole())) return <AutotestsAccessDeniedPage />
  return children
}

function V2ConnectionsRoute() {
  const location = useLocation()
  if (isAuthenticated(getRealm())) {
    if (!MANAGER_ROLES.includes(getStoredAuthRole())) {
      return <Navigate to={defaultPathForRole()} replace />
    }
    return <AdminV2Layout><AdminV2Connections /></AdminV2Layout>
  }
  return <Navigate to={`/connect/fanvue${location.search || ''}`} replace />
}

function V2FanvueCallbackRoute() {
  if (isAuthenticated(getRealm())) {
    return <AdminV2Layout><AdminV2FanvueCallback /></AdminV2Layout>
  }
  return <AdminV2FanvueCallback publicShell />
}

function getRouterBasename() {
  if (typeof window === 'undefined') return undefined
  if (!import.meta.env.DEV) return undefined
  const { hostname, pathname } = window.location
  const isDevHost = hostname === 'localhost' || hostname.startsWith('127.') || hostname === 'devu'
  if (!isDevHost) return undefined
  if (pathname.startsWith('/admin')) return '/admin'
  if (pathname.startsWith('/test')) return '/test'
  return undefined
}

export default function App() {
  const basename = getRouterBasename()

  return (
    <I18nProvider>
      <ToastProvider>
        <ErrorBoundary>
          <BrowserRouter basename={basename}>
            <WorkspaceScopeProvider>
              <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/connect/fanvue" element={<AdminV2PublicFanvueConnect />} />
              <Route path="/privacy" element={<PublicInformationPage page="privacy" />} />
              <Route path="/terms" element={<PublicInformationPage page="terms" />} />
              <Route path="/support" element={<PublicInformationPage page="support" />} />
              <Route path="/data-deletion" element={<PublicInformationPage page="deletion" />} />
              <Route path="/tools/chatter-score" element={<ChatterScorePage />} />
              <Route path="/chatter-score" element={<ChatterScorePage />} />
              <Route path="/v2/connections" element={<V2ConnectionsRoute />} />
              <Route path="/v2/connections/fanvue/callback" element={<V2FanvueCallbackRoute />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Navigate to={defaultPathForRole()} replace />
                  </ProtectedRoute>
                }
              />
              <Route path="/models" element={<LegacyV2Redirect target="/v2/models" />} />
              <Route path="/models/:id" element={<LegacyV2Redirect target={(pathname) => `/v2${pathname}`} />} />
              <Route path="/fyp" element={<LegacyV2Redirect target="/v2/fyp" />} />
              <Route path="/fans" element={<LegacyV2Redirect target="/v2/fans" />} />
              <Route path="/chats" element={<LegacyV2Redirect target="/v2/inbox" />} />
              <Route path="/analytics" element={<LegacyV2Redirect target="/v2/analytics" />} />
              <Route path="/purchases" element={<LegacyV2Redirect target="/v2/purchases" />} />
              <Route path="/logs/*" element={<LegacyV2Redirect target={(pathname) => `/v2${pathname}`} />} />
              <Route path="/autotests/*" element={<LegacyV2Redirect target={(pathname) => `/v2${pathname}`} />} />
              <Route path="/users" element={<LegacyV2Redirect target="/v2/users" />} />
              <Route path="/agency-settings" element={<LegacyV2Redirect target="/v2/agency-settings" />} />
              <Route path="/traffic-intelligence" element={<LegacyV2Redirect target="/v2/traffic-intelligence" />} />
              <Route path="/settings/configuration/*" element={<LegacyV2Redirect target={(pathname) => `/v2${pathname.replace('/settings', '')}`} />} />
              <Route path="/settings/*" element={<LegacyV2Redirect target="/v2/settings" />} />
              <Route
                path="/v2"
                element={
                  <ProtectedRoute>
                    <AdminV2Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<RoleRoute allowed={ANY_AUTHENTICATED_ROLES}><AdminV2Dashboard /></RoleRoute>} />
                <Route path="models" element={<RoleRoute allowed={ANY_AUTHENTICATED_ROLES}><AdminV2Models /></RoleRoute>} />
                <Route path="onboarding" element={<RoleRoute allowed={ONBOARDING_ROLES}><AdminV2Onboarding /></RoleRoute>} />
                <Route path="partner-onboarding" element={<RoleRoute allowed={ADMIN_ROLES}><PartnerOnboardingPage /></RoleRoute>} />
                <Route path="models/:id" element={<RoleRoute allowed={MANAGER_ROLES}><AdminV2ModelDetail /></RoleRoute>} />
                <Route path="fyp" element={<FanvueReviewRestrictedRoute><RoleRoute allowed={MANAGER_ROLES}><FanslyFypPage variant="v2" /></RoleRoute></FanvueReviewRestrictedRoute>} />
                <Route path="fans" element={<RoleRoute allowed={ANY_AUTHENTICATED_ROLES}><ModelDataPage type="fans" /></RoleRoute>} />
                <Route path="inbox" element={<RoleRoute allowed={ANY_AUTHENTICATED_ROLES}><AdminV2Inbox /></RoleRoute>} />
                <Route path="analytics" element={<RoleRoute allowed={TESTER_OR_ADMIN_ROLES}><AnalyticsPage /></RoleRoute>} />
                <Route path="purchases" element={<RoleRoute allowed={ANY_AUTHENTICATED_ROLES}><ModelDataPage type="purchases" /></RoleRoute>} />
                <Route path="content" element={<RoleRoute allowed={MANAGER_ROLES}><AdminV2Content /></RoleRoute>} />
                <Route path="customs" element={<RoleRoute allowed={MANAGER_ROLES}><AdminV2Customs /></RoleRoute>} />
                <Route path="automation" element={<RoleRoute allowed={MANAGER_ROLES}><AdminV2Automation /></RoleRoute>} />
                <Route path="settings" element={<RoleRoute allowed={MANAGER_ROLES}><AdminV2Settings /></RoleRoute>} />
                <Route path="diagnostics" element={<RoleRoute allowed={TESTER_OR_ADMIN_ROLES}><AdminV2Diagnostics /></RoleRoute>} />
                <Route path="logs/proactive" element={<RoleRoute allowed={TESTER_OR_ADMIN_ROLES}><ProactiveLogsPage /></RoleRoute>} />
                <Route path="logs" element={<RoleRoute allowed={TESTER_OR_ADMIN_ROLES}><LogsPage /></RoleRoute>} />
                <Route path="autotests" element={<AutotestsRoute><AutotestsPage /></AutotestsRoute>} />
                <Route path="autotests/prompts" element={<AutotestsRoute><AutotestsPromptsPage /></AutotestsRoute>} />
                <Route path="autotests/runs/:runId" element={<AutotestsRoute><AutotestsRunDetailPage /></AutotestsRoute>} />
                <Route path="autotests/analysis" element={<AutotestsRoute><AutotestsAnalysisPage /></AutotestsRoute>} />
                <Route path="graph" element={<Navigate to={isAdminRole() ? '/v2/configuration' : defaultPathForRole()} replace />} />
                <Route path="users" element={<AdminOnlyRoute><SettingsUsers /></AdminOnlyRoute>} />
                <Route path="agency-settings" element={<RoleRoute allowed={[...ADMIN_ROLES, 'agency']}><AgencySettingsPage /></RoleRoute>} />
                <Route path="traffic-intelligence" element={<RoleRoute allowed={[...ADMIN_ROLES, 'agency']}><TrafficIntelligencePage /></RoleRoute>} />
                <Route path="configuration" element={<AdminOnlyRoute><SettingsLayout /></AdminOnlyRoute>}>
                  <Route index element={<SettingsConfiguration />} />
                  <Route path=":configId" element={<SettingsConfigDetailLayout />}>
                    <Route index element={<Navigate to="graph" replace />} />
                    <Route path="graph" element={<SettingsConfigGraph />} />
                    <Route path="agents" element={<SettingsAgentsLayout />}>
                      <Route index element={<Navigate to="message_analyzer" replace />} />
                      <Route path=":agentKey" element={<SettingsAgentDetail />} />
                    </Route>
                    <Route path="nodes" element={<SettingsNodesLayout />}>
                      <Route index element={<Navigate to="in" replace />} />
                      <Route path=":nodeKey" element={<SettingsNodeDetail />} />
                    </Route>
                    <Route path="edges" element={<Navigate to="nodes/media_selector" replace />} />
                    <Route path="variables" element={<Navigate to="nodes/core" replace />} />
                    <Route path="proactive" element={<SettingsProactive />} />
                    <Route path="settings" element={<SettingsExecutorPage />} />
                    <Route path="other" element={<Navigate to="nodes" replace />} />
                  </Route>
                </Route>
              </Route>
              <Route
                path="/old"
                element={
                  <ProtectedRoute>
                    <FanvueReviewRestrictedRoute><Layout /></FanvueReviewRestrictedRoute>
                  </ProtectedRoute>
                }
              >
                <Route index element={<IndexOrRedirect />} />
                <Route path="models" element={<RoleRoute allowed={MANAGER_ROLES}><Models /></RoleRoute>} />
                <Route path="models/:id" element={<RoleRoute allowed={MANAGER_ROLES}><ModelDetailOrRedirect /></RoleRoute>} />
                <Route path="fyp" element={<RoleRoute allowed={MANAGER_ROLES}><FanslyFypPage /></RoleRoute>} />
                <Route path="fans" element={<RoleRoute allowed={ANY_AUTHENTICATED_ROLES}><ModelDataPage type="fans" /></RoleRoute>} />
                <Route path="chats" element={<RoleRoute allowed={ANY_AUTHENTICATED_ROLES}><ChatsPage /></RoleRoute>} />
                <Route path="analytics" element={<RoleRoute allowed={TESTER_OR_ADMIN_ROLES}><AnalyticsPage /></RoleRoute>} />
                <Route path="purchases" element={<RoleRoute allowed={ANY_AUTHENTICATED_ROLES}><ModelDataPage type="purchases" /></RoleRoute>} />
                <Route path="logs/proactive" element={<RoleRoute allowed={TESTER_OR_ADMIN_ROLES}><ProactiveLogsPage /></RoleRoute>} />
                <Route path="logs" element={<RoleRoute allowed={TESTER_OR_ADMIN_ROLES}><LogsPage /></RoleRoute>} />
                <Route path="autotests" element={<AutotestsRoute><AutotestsPage /></AutotestsRoute>} />
                <Route path="autotests/prompts" element={<AutotestsRoute><AutotestsPromptsPage /></AutotestsRoute>} />
                <Route path="autotests/runs/:runId" element={<AutotestsRoute><AutotestsRunDetailPage /></AutotestsRoute>} />
                <Route path="autotests/analysis" element={<AutotestsRoute><AutotestsAnalysisPage /></AutotestsRoute>} />
                <Route path="graph" element={<Navigate to={isAdminRole() ? '/old/settings/configuration' : defaultPathForRole()} replace />} />
                <Route path="users" element={<AdminOnlyRoute><SettingsUsers /></AdminOnlyRoute>} />
                <Route path="agency-settings" element={<RoleRoute allowed={[...ADMIN_ROLES, 'agency']}><AgencySettingsPage /></RoleRoute>} />
                <Route path="traffic-intelligence" element={<RoleRoute allowed={[...ADMIN_ROLES, 'agency']}><TrafficIntelligencePage /></RoleRoute>} />
                <Route path="settings" element={<SettingsOrRedirect />}>
                  <Route index element={<Navigate to="configuration" replace />} />
                  <Route path="users" element={<AdminOnlyRoute><SettingsUsers /></AdminOnlyRoute>} />
                  <Route path="configuration" element={<SettingsConfiguration />} />
                  <Route path="configuration/:configId" element={<SettingsConfigDetailLayout />}>
                    <Route index element={<Navigate to="graph" replace />} />
                    <Route path="graph" element={<SettingsConfigGraph />} />
                    <Route path="agents" element={<SettingsAgentsLayout />}>
                      <Route index element={<Navigate to="message_analyzer" replace />} />
                      <Route path=":agentKey" element={<SettingsAgentDetail />} />
                    </Route>
                    <Route path="nodes" element={<SettingsNodesLayout />}>
                      <Route index element={<Navigate to="in" replace />} />
                      <Route path=":nodeKey" element={<SettingsNodeDetail />} />
                    </Route>
                    <Route path="edges" element={<Navigate to="nodes/media_selector" replace />} />
                    <Route path="variables" element={<Navigate to="nodes/core" replace />} />
                    <Route path="proactive" element={<SettingsProactive />} />
                    <Route path="settings" element={<SettingsExecutorPage />} />
                    <Route path="other" element={<Navigate to="nodes" replace />} />
                  </Route>
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </WorkspaceScopeProvider>
          </BrowserRouter>
        </ErrorBoundary>
      </ToastProvider>
    </I18nProvider>
  )
}
